import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { getDefaultAnalyticsSettings } from 'core/utils/analyticsUtils';
import { currencyFormatter, formatCompactNumber } from 'core/utils/commonUtils';
import { useEffect, useState } from 'react';
import { analyticsService } from 'services/analyticsService';
import { getDateLabel } from '../utils';
import ChartCard from './ChartCard';
import ChartSettingsDialog from './ChartSettingsDialog';
import type { ChartSettings } from 'models/analytics/chartSettings';
import type { RevenueAnalytic } from 'models/analytics/revenueAnalytic';
import type { FC } from 'react';

const RevenueChart: FC = () => {
  const [data, setData] = useState<RevenueAnalytic[] | null>(null);
  const [settings, setSettings] = useState<ChartSettings>(
    getDefaultAnalyticsSettings().revenue
  );
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const revenue = await analyticsService.getRevenueAnalytic({
        from: settings.dateRange.from,
        to: settings.dateRange.to,
        groupBy: settings.groupBy,
      });
      setData(revenue);
    };
    void fetchData();
  }, [settings]);

  const handleSettingsSave = (newSettings: ChartSettings): void => {
    setSettings(newSettings);
    setSettingsDialogOpen(false);
  };

  if (!data) return null;

  return (
    <ChartCard
      title='Динамика выручки'
      onSettingsClick={() => {
        setSettingsDialogOpen(true);
      }}>
      <Box height={400}>
        <BarChart
          series={[
            {
              data: data.map((item) => item.revenue),
              label: 'Выручка',
              valueFormatter: (value) => currencyFormatter.format(value ?? 0),
              color: '#4CAF50',
            },
          ]}
          xAxis={[
            {
              data: data.map((item) => getDateLabel(item.date, settings.groupBy)),
              scaleType: 'band',
            },
          ]}
          yAxis={[
            {
              tickMinStep: 10000,
              valueFormatter: (value) => formatCompactNumber(value),
            },
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'right' },
              padding: 0,
            },
          }}
          sx={{
            '.MuiBarElement-root': {
              rx: '5px',
            },
          }}
        />
      </Box>
      <ChartSettingsDialog
        open={settingsDialogOpen}
        settings={settings}
        onClose={() => {
          setSettingsDialogOpen(false);
        }}
        onSave={handleSettingsSave}
        title='Настройка графика "Динамики выручки"'
      />
    </ChartCard>
  );
};

export default RevenueChart;
